import React, { useState, useEffect } from 'react';
import {
  Typography, TextField, FormControlLabel, Checkbox, Box, InputAdornment, CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckboxTree, { Node } from 'react-checkbox-tree';
import { useTheme } from '@mui/material/styles';
import { getStyles } from './styles';

import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ProvisionSelectProps {
  nodes: Node[];
  preSelectedNodeValues: string[];
  onChange: (selectedNodes: string[]) => void;
  rosterWarning: string;
  isLoading: boolean;
  loadingProgress?: string;
  filterGrades?: string[];
  hideExpandAll?: boolean;
  precheckedNodes?: Node[];
}

export default function ProvisionTree({ onChange: handleCheckChange, loadingProgress, ...props }: ProvisionSelectProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const initialValue: string[] = [];
  const emptyNodesValue: Node[] = [];

  const [checked, setChecked] = useState(initialValue);
  const [expanded, setExpanded] = useState(initialValue);
  const [filterText, setFilterText] = useState('');
  const [nodesFiltered, setNodesFiltered] = useState(emptyNodesValue);
  const [nodes, setNodes] = useState(emptyNodesValue);
  const [initialized, setInitialized] = useState(false);
  const [shouldFilter, setShouldFilter] = useState(false);
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    const nodesToDisplay = props.precheckedNodes ?? props.nodes;
    setNodes(nodesToDisplay);
    setNodesFiltered(nodesToDisplay);
    setNodeView(nodesToDisplay, props.preSelectedNodeValues);
  }, [props.nodes, props.preSelectedNodeValues, props.precheckedNodes]);

  const setNodeView = ((nodes: Node[], selections: string[]) => {
    const preSelectedLeafValues = nodes.reduce(getMatchingLeafValues, []);
    setChecked(preSelectedLeafValues);

    /**
     * Reduction function to get all matching leaf values.
     * TODO May want to consider using a Set throughout the reduction to ensure uniqueness.
     *
     * @param matchedLeafValues An array of all the matched leaf values
     * @param node The Node object to walk
     *
     * @returns A flat array of all the collected leaf string values
     */
    function getMatchingLeafValues(matchedLeafValues: string[], node: Node): string[] {
      if (node.children && node.children.length) {
        // This is not a leaf node, so descend...
        node.children.reduce(getMatchingLeafValues, matchedLeafValues);
        return matchedLeafValues;
      }

      const isMatched = selections.some(selection => node.value.startsWith(selection + '|') || node.value === selection);

      if (isMatched) {
        // Found a matching leaf value, add it to the rest
        matchedLeafValues.push(node.value);
      }

      return matchedLeafValues;
    }
  });

  const onCheck = ((checkedNodes: string[]) => {
    if (allSelected) {
      setAllSelected(false);
    }
    setChecked(checkedNodes);
    handleCheckChange(checkTopNodeV2(checkedNodes));
  });

  const checkTopNodeV2 = ((checked: string[]) => {

    let parentsToCheck = true;
    const checkedNodes = checked.slice(0);

    while (parentsToCheck) {
      parentsToCheck = false;

      for (const checkedNode of checkedNodes) {
        if (checkedNode.lastIndexOf('|') > 0) {
          const parent = getNodeV2(checkedNode.substring(0, checkedNode.lastIndexOf('|')));
          if (
            parent &&
            parent.children &&
            isEveryChildCheckedV2(parent, checkedNodes)
          ) {
            parentsToCheck = true;
            checkedNodes.push(parent.value);
            const splicePositions: number[] = [];
            parent.children.forEach((child) => splicePositions.push(checkedNodes.indexOf(child.value)));
            const reversedSplicePositions = splicePositions.sort((a, b) => b - a);
            reversedSplicePositions.forEach(splicePos => checkedNodes.splice(splicePos, 1));
          }
        }
      }
    }

    const uniqueNodes = Array.from(new Set(checkedNodes));
    return uniqueNodes;
  });

  const getNodeV2 = ((nodeValue: string, nodesToSearch: Node[] = nodes): Node => {
    let node = nodesToSearch.find(n => n.value === nodeValue);
    const newNodes = new Array<Node>();

    if (!node) {
      nodesToSearch.forEach((n) => {
        if (n.children) {
          n.children.forEach((c => newNodes.push(c)));
        }
      });
      node = getNodeV2(nodeValue, newNodes);
    }

    return node;
  });

  const isEveryChildCheckedV2 = ((node: Node, checkedNodes: string[]) => {
    if (node.children) {
      return node.children.every(child => {
        return checkedNodes.includes(child.value) &&
          !(props.filterGrades && props.filterGrades.some((g) => child.value.endsWith(`grade:${g}`)))
      });
    }
  });

  const onExpand = ((expandedNodes: string[]) => {
    setExpanded(expandedNodes);
  });

  const onFilterChange = ((e: any) => {
    setFilterText(e.target.value);
  });

  useEffect(() => {
    const filterNodes = ((filtered: any, node: any) => {
      const children = (node.children || []).reduce(filterNodes, []);
      if (
        // Node's label matches the search string
        (node.label.toLocaleLowerCase().indexOf(filterText.toLocaleLowerCase()) > -1 &&
          //If we are filtering grades, nodes grade is part of the filter
          (!props.filterGrades || (props.filterGrades && props.filterGrades.some((g) => (node.value + '|').includes(`grade:${g}|`))))) ||
        // Or a children has a matching node
        children.length
      ) {
        if (children.length) {
          filtered.push({ ...node, children });
        } else {
          filtered.push({ ...node });
        }
      }

      return filtered;
    });

    const getNodesToExpand = ((expanded: any, nodes: any) => {
      nodes.forEach((node: any) => {
        if (node.label.toLocaleLowerCase().indexOf(filterText.toLocaleLowerCase()) === -1) {
          expanded.push(node.value);

          const children = node.children || [];

          if (children.length) {
            children.forEach((child: any) => {
              getNodesToExpand(expanded, [child])
            })
          }
        }
      })

      return expanded;
    })

    const filterTree = (() => {
      setInitialized(true);

      // Reset nodes back to unfiltered state
      if (!filterText && !props.filterGrades) {
        setNodesFiltered(nodes);
        return;
      }

      const filteredNodes = nodes.reduce(filterNodes, []);
      const nodesToExpand = getNodesToExpand(expanded, filteredNodes);
      setExpanded(nodesToExpand);
      setNodesFiltered(filteredNodes);
    });

    if (initialized || filterText || props.filterGrades) {
      filterTree();
    }
  }, [shouldFilter, nodes, initialized, filterText, props.filterGrades]);


  const selectAll = () => {
    const getAllNodes = ((selected: any, nodes: any) => {
      nodes.forEach((node: any) => {
        const children = node.children || [];

        if (children.length) {
          children.forEach((child: any) => {
            getAllNodes(selected, [child])
          })
        } else {
          selected.push(node.value);
        }
      })

      return selected;
    });

    const allNodes = nodesFiltered.length ? nodesFiltered : nodes;
    const selected = getAllNodes([], allNodes);
    const topNodes = checkTopNodeV2(selected);
    setChecked(topNodes);
    handleCheckChange(topNodes);
    setAllSelected(true);
  }

  const deselectAll = () => {
    const topNodes = checkTopNodeV2(initialValue);
    setChecked(topNodes);
    handleCheckChange(topNodes);
    setAllSelected(false);
  }

  return (
    <Typography component='div' className='filter-container'>
      <Box sx={styles.provisionTreeContainer}>
        <Box
          component='span'
          sx={[{
            display: 'inline',
            m: 2
          }, styles.searchContainer]}>
          <TextField
            label='Search'
            variant='outlined'
            id='filterText'
            sx={styles.search}
            slotProps={{
              input: {
                value: filterText,
                endAdornment: (
                  <InputAdornment position='end'>
                    <SearchIcon sx={styles.searchIcon} />
                  </InputAdornment>
                ),
              }
            }}
            onChange={onFilterChange}
            onKeyPress={(e: any) => {
              if (e.key === "Enter") {
                setShouldFilter(!shouldFilter);
              }
            }}
          />
        </Box>
        <Typography
          component='div'
          sx={styles.checkboxTreeContainer}
        >
          {nodes.length > 1 && props.rosterWarning.length === 0 && !props.isLoading ? <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                onChange={allSelected ? deselectAll : selectAll}
                name="checkedB"
                color="primary"
              />
            }
            label="Select All"
          /> : null}
          {props.isLoading ? <Box sx={styles.loaderContainer}><CircularProgress />{loadingProgress}</Box> : null}
          {props.rosterWarning.length === 0 && nodesFiltered.length === 0 && !props.isLoading ? <Typography>No data to display...</Typography> : null}
          {props.rosterWarning.length > 0 && !props.isLoading ?
            <Typography>{props.rosterWarning}</Typography> :
            <CheckboxTree
              id={'cbx-tree'}
              icons={{
                check: <FontAwesomeIcon className='rct-icon rct-icon-check' icon={['far', 'check-square']} />,
                uncheck: <FontAwesomeIcon className='rct-icon rct-icon-uncheck' icon={['far', 'square']} />,
                halfCheck: <FontAwesomeIcon className='rct-icon rct-icon-half-check' icon={['far', 'minus-square']} />,
                expandClose: <FontAwesomeIcon className='rct-icon rct-icon-expand-close' icon={['fas', 'chevron-right']} />,
                expandOpen: <FontAwesomeIcon className='rct-icon rct-icon-expand-open' icon='chevron-down' />,
                expandAll: <FontAwesomeIcon className='rct-icon rct-icon-expand-all' icon='plus-square' />,
                collapseAll: <FontAwesomeIcon className='rct-icon rct-icon-collapse-all' icon='minus-square' />,
              }}
              checked={checked}
              expanded={expanded}
              nodes={nodesFiltered}
              onCheck={onCheck}
              onExpand={onExpand}
              showExpandAll={props.hideExpandAll ? false : true}
              showNodeIcon={false}
            />}
        </Typography>
      </Box>
    </Typography>
  );
}